import * as React from 'react';
import { cn } from '@/lib/utils';

/**
 * A labelled figure in a bordered tile — the app's most-copied small component.
 *
 * Four local `Stat`/`StatTile` definitions existed before this file. Three were
 * tiles and are now this primitive; the fourth (SportmonksSyncPanel) is an
 * inline `label: value` span that only shares the name, and was left alone.
 *
 * The two shipped tile designs are kept as variants rather than collapsed into
 * one, because they are different intents, not drift:
 *
 *   feature — the event header's three tappable stats. Gold, large, tappable.
 *   dense   — the admin summary strips. Cream, small, informational.
 *
 * Collapsing them would have repainted every admin stat gold, which is the
 * colour this app reserves for the player's own money and standing.
 */
export type StatVariant = 'feature' | 'dense';

/**
 * `warn` is the admin claims strip's alert state (clawbacks, open flags).
 * It repaints the border and the value together — a value colour alone reads
 * as emphasis, not as a warning, once several tiles sit side by side.
 */
export type StatTone = 'default' | 'warn';

export const STAT_VARIANT = {
  feature: {
    container: 'rounded-card px-3 py-2.5',
    label: 'text-[10px] text-tertiary uppercase tracking-wider',
    value: 'text-lg sm:text-xl font-bold leading-tight truncate',
    valueColor: 'text-gold',
  },
  dense: {
    container: 'rounded-control px-3 py-2',
    label: 'text-[10px] text-warm-gray/50 uppercase tracking-wider',
    value: 'text-base font-bold',
    valueColor: 'text-cream',
  },
} as const satisfies Record<StatVariant, Record<string, string>>;

export interface StatProps {
  label: React.ReactNode;
  value: React.ReactNode;
  /** Secondary line under the value — rank, streak, count. */
  sub?: React.ReactNode;
  variant?: StatVariant;
  tone?: StatTone;
  /**
   * Overrides the variant's value colour. This is why the primitive exists in
   * the shape it does: the survivor header renders ALIVE in verdigris and OUT
   * in oxblood, so the value colour cannot be owned solely by the variant.
   * Wins over `tone` when both are set.
   */
  valueClass?: string;
  labelClass?: string;
  subClass?: string;
  /** Makes the tile a button. Omit for a static tile. */
  onClick?: () => void;
  className?: string;
}

export function Stat({
  label,
  value,
  sub,
  variant = 'feature',
  tone = 'default',
  valueClass,
  labelClass,
  subClass,
  onClick,
  className,
}: StatProps) {
  const v = STAT_VARIANT[variant];
  const warn = tone === 'warn';

  const content = (
    <>
      <p className={cn(v.label, labelClass)}>{label}</p>
      {/*
        `tabular-nums` is the one thing every copy of this component was
        missing and the reason it is worth centralising: the event header's
        score and alive-count tick live, and proportional figures make the
        whole tile jitter sideways on every update.
      */}
      <p className={cn(v.value, 'tabular-nums', valueClass ?? (warn ? 'text-warning' : v.valueColor))}>
        {value}
      </p>
      {sub && <p className={cn('text-[11px] leading-tight tabular-nums', subClass ?? 'text-warm-gray')}>{sub}</p>}
    </>
  );

  const base = cn(
    'min-w-0 bg-surface-2 border text-left',
    warn ? 'border-warning/40' : 'border-surface-3',
    v.container,
    className
  );

  if (!onClick) return <div className={base}>{content}</div>;

  return (
    <button type="button" onClick={onClick} className={cn(base, 'hover:border-gold/30 transition-colors')}>
      {content}
    </button>
  );
}
